import getAllFiltered from "@api/graphql/generic/get-all-filtered";
import { downloadDeliveryInvoices } from "@api/Rest/download/deliveryInvoicesDownload";
import { Button, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { PaginationState } from "@tanstack/react-table";
import type { GetAllInput, OrderType } from "@types_def/common/filter.types";
import type { QueryType } from "@types_def/common/query.types";
import type { DeliveryPayment } from "@types_def/models/deliveryPayment.types";
import { useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { DataTable } from "@/components/ui/data-table";
import { DataTableToolbar } from "@/components/ui/data-table-toolbar";
import { useDataTable } from "@/hooks/use-data-table";

import { DELIVERY_PAYMENT_COLUMNS } from "./columns";

function DeliveryInvoicesTable() {
  "use no memo";
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isSubscription = useLocation().pathname.includes("subscription");

  const pageIndex = Number.parseInt(searchParams.get("page") ?? "0", 10) || 0;
  const orderBy = (searchParams.get("orderBy") as keyof DeliveryPayment | null) ?? "id";
  const order = searchParams.get("order") === "asc" ? "asc" : "desc";

  const pageSize = Number.parseInt(searchParams.get("pageSize") ?? "10", 10) || 10;

  const invoiceTableQueryFields: QueryType<DeliveryPayment> = {
    id: true,
    createdAt: true,
    amount: true,
    status: true,
    deliveryId: true,
    driver: {
      fullName: true,
    },
  };

  const queryInput = useMemo<GetAllInput<DeliveryPayment, keyof DeliveryPayment>>(
    () => ({
      keyword: "",
      pagination: { page: pageIndex, perPage: pageSize },
      sort: { field: orderBy, order: order as OrderType },
      include: {
        driver: true,
      },
    }),
    [pageIndex, pageSize, orderBy, order]
  );

  const { data, isPending, isError } = useQuery({
    queryKey: ["delivery-payment-table", queryInput],
    queryFn: async () => {
      const response = await getAllFiltered<DeliveryPayment, keyof DeliveryPayment>(
        queryInput,
        "DeliveryPayments",
        invoiceTableQueryFields
      );
      return response.data;
    },
  });

  const pageCount = Math.ceil((data?.total ?? 0) / pageSize);

  const handlePaginationChange = (pagination: PaginationState) => {
    const nextParams = new URLSearchParams(searchParams);

    if (pagination.pageIndex > 0) {
      nextParams.set("page", `${pagination.pageIndex}`);
    } else {
      nextParams.delete("page");
    }

    if (pagination.pageSize !== 10) {
      nextParams.set("pageSize", String(pagination.pageSize));
    } else {
      nextParams.delete("pageSize");
    }

    setSearchParams(nextParams);
  };

  const handleSortingChange = (sorting: Array<{ id: string; desc: boolean }>) => {
    const nextParams = new URLSearchParams(searchParams);
    const nextSort = sorting[0];

    nextParams.delete("page");

    if (nextSort !== undefined) {
      nextParams.set("orderBy", nextSort.id);
      nextParams.set("order", nextSort.desc ? "desc" : "asc");
    } else {
      nextParams.delete("orderBy");
      nextParams.delete("order");
    }

    setSearchParams(nextParams);
  };

  const { table } = useDataTable<DeliveryPayment>({
    data: data?.records ?? [],
    columns: DELIVERY_PAYMENT_COLUMNS,
    pageCount,
    manualPagination: true,
    manualSorting: true,
    externalPagination: { pageIndex, pageSize },
    externalSorting: [{ id: orderBy, desc: order === "desc" }],
    onPaginationChange: handlePaginationChange,
    onSortingChange: handleSortingChange,
  });

  const handleExport = () => {
    downloadDeliveryInvoices({
      keyword: "",
      pagination: { page: pageIndex, perPage: pageSize },
      sort: { field: "id", order: order as OrderType },
      include: {
        driver: true,
      },
    });
  };

  return (
    <Stack gap={2} sx={{ p: 2 }}>
      <Stack direction="row" sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <Button
          color="secondary"
          onClick={() => {
            void navigate("/admin/invoices/driver/subscription");
          }}
          variant={!isSubscription ? "outlined" : "contained"}
        >
          Subscription
        </Button>

        <Button
          color="secondary"
          onClick={() => {
            void navigate("/admin/invoices/driver/delivery");
          }}
          variant={isSubscription ? "outlined" : "contained"}
        >
          Livraison
        </Button>
      </Stack>

      <DataTable isLoading={isPending} table={table} totalCount={data?.total ?? 0}>
        <DataTableToolbar table={table}>
          <Button onClick={handleExport} size="small" variant="outlined">
            Export
          </Button>
        </DataTableToolbar>
      </DataTable>

      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        {isPending ? <Typography variant="body2">Loading...</Typography> : null}
        {isError ? (
          <Typography color="error" variant="body2">
            Failed to load delivery invoices.
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
}

export default DeliveryInvoicesTable;
